import { map, not, readOnly } from '@ember/object/computed';

import Model, { attr, belongsTo, hasMany } from '@warp-drive/legacy/model';
import { TrackedObject } from 'tracked-built-ins';

import slugify from 'umg-ember-backend/utils/slugify';
import {
  buildValidations,
  validator,
} from 'umg-ember-backend/utils/validations';

const fontFamiliesToLoad = map(
  'model.mlp.options.fontFamiliesToLoad',
  (fontFamily) => {
    return fontFamily.familyWithFallback;
  },
);

const Validations = buildValidations({
  contentItems: [validator('has-many')],
  link: validator('format', {
    disabled: readOnly('model.linkValidationDisabled'),
    type: 'url',
  }),
  linkTarget: validator('presence', {
    disabled: readOnly('model.linkValidationDisabled'),
    presence: true,
  }),
  linkType: validator('presence', true),
  name: validator('presence', true),
  ['options.fontFamily']: validator('inclusion', {
    in: fontFamiliesToLoad,
    allowBlank: true,
    disabled: not('model.mlp.isV2'),
  }),
  ['options.headlineFontFamily']: validator('inclusion', {
    in: fontFamiliesToLoad,
    allowBlank: true,
    disabled: not('model.mlp.isV2'),
  }),
});

export const settings = {
  copyableOptions(record) {
    return {
      overwrite: {
        options: {
          ...record.options,
        },
      },
      relationsToCopy: ['contentItems'],
    };
  },
};

export default class MlpPageModel extends Model.extend(Validations) {
  @attr('string') link;
  @attr('string', { defaultValue: '_self' }) linkTarget;
  @attr('string', { defaultValue: 'internal' }) linkType;
  @attr('string') name;
  @attr('json', {
    defaultValue() {
      return new TrackedObject({});
    },
  })
  options;
  @attr('number') position;
  @attr('boolean') start;
  @attr('boolean', { defaultValue: true }) visible;

  // CMS fields
  @attr('string') seoTitle;
  @attr('string') seoDescription;

  // Relation
  @belongsTo('artist', { async: false, inverse: null }) artist;
  @belongsTo('asset', { async: true, inverse: null }) backgroundImage;
  @belongsTo('asset', { async: true, inverse: null }) backgroundVideo;
  @hasMany('content-item', { async: false, inverse: 'page' }) contentItems;
  @belongsTo('mlp', { async: false, inverse: 'pages' }) mlp;
  @belongsTo('asset', { async: true, inverse: null }) seoImage;

  // Getter and setter
  get cmsModified() {
    return this.mlp?.cmsModified;
  }

  get displayHeadline() {
    return this.name;
  }

  get displayLabel() {
    return this.name;
  }

  get isArtist() {
    return this.linkType === 'artist';
  }

  get isExternal() {
    return this.linkType === 'external';
  }

  get isInternal() {
    return this.linkType === 'internal';
  }

  get linkValidationDisabled() {
    return this.isInternal || this.isArtist;
  }

  get slug() {
    return `/${slugify(this.name)}`;
  }

  // Functions
  resetOptions() {
    for (const key in this.options) {
      delete this.options[key];
    }

    this.backgroundImage = null;
    this.backgroundVideo = null;
  }

  setSlugAsLink() {
    if (this.start) {
      this.link = '/';
    } else if (this.isInternal) {
      this.link = this.slug;
    }
  }
}
